import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { format } from 'date-fns';
import { getRtiPetchem, getPEFeedstockForecasts } from '@/services/peService';
import { useRsColors } from '@/hooks/useRsColors';
import { parseDateLiteral, inventoryChangeAxisLabel } from '@/utils/chartAxisUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const MAIN_FEEDSTOCK = { label: 'WTI Crude Oil', dataKey: 'wti_crude_oil', unit: '$/bbl' };
const SUB_FEEDSTOCKS = [
  { label: 'Spot Ethylene', dataKey: 'spot_ethylene', unit: '¢/lb' },
  { label: 'Nat. Gas', dataKey: 'nat_gas', unit: '$/MMBtu' },
  { label: 'Ethane', dataKey: 'ethane', unit: '¢/gal' },
  { label: 'Brent Crude', dataKey: 'brent_crude_oil', unit: '$/bbl' },
];

const SUB_COLORS = [
  'rgba(59,130,246,0.9)',
  'rgba(16,185,129,0.9)',
  'rgba(245,158,11,0.9)',
  'rgba(234,179,8,0.9)',
];

interface KpiCardProps {
  label: string;
  value?: number | null;
  unit?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, unit = '$/lb' }) => (
  <div className="bg-rs-off-black rounded-lg p-3 min-w-[120px]">
    <p className="text-rs-light-gray text-xs mb-1">{label}</p>
    <p className="text-rs-green font-bold text-lg">
      {value !== null && value !== undefined ? `$${value.toFixed(2)}` : '—'}
    </p>
    <p className="text-rs-highlight-gray text-xs">{unit}</p>
  </div>
);

const buildChartData = (source: any[], dataKey: string, color: string) => {
  const labels = source.map((d) => {
    const dateStr = d.date || d.Date;
    try { return format(new Date(dateStr + 'T00:00:00'), 'MMM yyyy'); } catch { return dateStr; }
  });
  const data = source.map((d) => {
    const val = d[dataKey];
    return val !== null && val !== undefined ? Number(val) : null;
  });
  return {
    labels,
    datasets: [{
      label: dataKey,
      data,
      borderColor: color,
      backgroundColor: 'transparent',
      pointRadius: 0,
      borderWidth: 2,
      tension: 0.3,
      spanGaps: true,
    }],
  };
};

const PEFeedstocksTab: React.FC<{ dateFrom?: string; dateTo?: string }> = ({
  dateFrom,
  dateTo,
}) => {
  const [petchem, setPetchem] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // Below 750px, show charts in 1 column (stacked); above 750px, show 2 columns.
  const [isSmallGrid, setIsSmallGrid] = useState(false);
  const hasFetched = useRef(false);
  const rs = useRsColors();

  const lineChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'index' as const, intersect: false },
      datalabels: { display: false },
    },
    scales: {
      x: {
        ticks: {
          color: rs.offWhite,
          font: { size: 9 },
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
          callback: function (this: any, value: any, index: number) {
            const lbl = this.getLabelForValue(value);
            // Collapse consecutive duplicate month labels (data may be daily)
            if (index > 0 && this.getLabelForValue(index - 1) === lbl) return '';
            const d = parseDateLiteral(lbl);
            if (isNaN(d.getTime())) return index % 3 === 0 ? lbl : '';
            // Per-date (no slider on this chart): <3yr → 6-month MMM yyyy, >3yr → year only
            const cutoff = new Date();
            cutoff.setFullYear(cutoff.getFullYear() - 3);
            return inventoryChangeAxisLabel(d, d < cutoff);
          },
        },
        grid: { color: rs.gridLine },
      },
      y: { ticks: { color: rs.offWhite, font: { size: 9 } }, grid: { color: rs.gridLine } },
    },
  }), [rs]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 749px)');
    // Set initial state immediately
    setIsSmallGrid(mq.matches);

    // Listen for changes
    const handler = (e: MediaQueryListEvent) => setIsSmallGrid(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchAll = async () => {
      setLoading(true);
      try {
        const [petchemData] = await Promise.all([
          getRtiPetchem(),
          getPEFeedstockForecasts(dateFrom, dateTo).catch(() => []),
        ]);
        setPetchem(Array.isArray(petchemData) ? petchemData : []);
      } catch (err) {
        console.error('PEFeedstocksTab fetch error', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-rs-off-white/50">
        Loading feedstock data…
      </div>
    );
  }

  const latest = petchem.length > 0 ? petchem[petchem.length - 1] : null;

  const mainChartData = buildChartData(petchem, MAIN_FEEDSTOCK.dataKey, 'rgba(132,204,22,0.9)');

  return (
    <div className="p-4 flex flex-col gap-4">
      {/* KPI cards */}
      <div className={`flex flex-wrap gap-3 ${isSmallGrid ? 'justify-center' : ''}`} data-testid="feedstocks-kpi-cards">
        <KpiCard
          label="WTI Crude"
          value={latest?.wti_crude_oil ?? latest?.['WTI Crude Oil'] ?? null}
          unit="$/bbl"
        />
        <KpiCard
          label="Brent Crude"
          value={latest?.brent_crude_oil ?? latest?.['Brent Crude Oil'] ?? null}
          unit="$/bbl"
        />
        <KpiCard
          label="Spot Ethylene"
          value={latest?.spot_ethylene ?? latest?.['Spot Ethylene'] ?? null}
          unit="¢/lb"
        />
        <KpiCard
          label="Nat. Gas"
          value={latest?.nat_gas ?? latest?.['Nat. Gas'] ?? null}
          unit="$/MMBtu"
        />
      </div>

      {/* Main feedstock chart — WTI Crude (full width) */}
      <div className="bg-rs-off-black/40 border border-rs-jet-light rounded-lg p-4">
        <h3 className="text-rs-off-white font-semibold mb-1 text-sm">{MAIN_FEEDSTOCK.label}</h3>
        <p className="text-rs-off-white/50 text-xs mb-3">{MAIN_FEEDSTOCK.unit}</p>
        <div className="h-64" data-testid="feedstocks-chart">
          <Line data={mainChartData} options={lineChartOptions} />
        </div>
      </div>

      {/* Sub feedstock charts — 2x2 grid above 750px, single column below */}
      <div className={isSmallGrid ? 'flex flex-col gap-4' : 'grid gap-4'} style={!isSmallGrid ? { gridTemplateColumns: 'repeat(2, 1fr)' } : {}}>
        {SUB_FEEDSTOCKS.map((fs, i) => {
          const subChartData = buildChartData(petchem, fs.dataKey, SUB_COLORS[i]);
          return (
            <div key={fs.dataKey} className="bg-rs-off-black/40 border border-rs-jet-light rounded-lg p-3">
              <p className="text-rs-off-white/70 text-xs mb-1">{fs.label}</p>
              <p className="text-rs-off-white/40 text-xs mb-2">{fs.unit}</p>
              <div className="h-40">
                <Line data={subChartData} options={lineChartOptions} />
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-rs-highlight-gray text-xs">Source: RTI PetChem / EIA STEO / Chemorbis</p>
    </div>
  );
};

export default PEFeedstocksTab;
